import { type Article } from '~/types/article';
import { type InlineAd } from '~/types/articleInline';

/**
 * ManualInlineAdProcessor
 *
 * Handles manual inline ad placement. When article.metadata.manuallyPlacedAds is true,
 * the article body is expected to contain placeholder elements with type === 'ad'.
 *
 * These placeholders are replaced in order with the corresponding ad configs from
 * articleAdConfig.inline[]. If the ad config for a placeholder should not be shown
 * (shouldShowAd returns false), the placeholder is removed from the body.
 *
 * No character-counting or position-checking is performed — the editorial team
 * is responsible for the placement positions.
 */
export default class ManualInlineAdProcessor {
  private readonly articleAdConfig: any;

  constructor (articleAdConfig: any) {
    this.articleAdConfig = articleAdConfig;
  }

  /*
   * ---------------------------------------------------------------------------
   * Public entry point
   * ---------------------------------------------------------------------------
   */

  process (article: Article): void {
    if (!article.body?.length || !this.articleAdConfig?.inline?.length) { return; }

    const inlineAdConfigs: any[] = this.articleAdConfig.inline;
    let adConfigIndex = 0;

    for (let i = 0; i < article.body.length; i++) {
      const element = article.body[i] as any;

      // Skip non-placeholder elements and already-injected ads
      if (element?.type !== 'ad' || element?.ad?.injected) {
        continue;
      }

      if (adConfigIndex >= inlineAdConfigs.length) {
        // No more ad configs — remove remaining placeholders
        article.body.splice(i, 1);
        i--;
        continue;
      }

      const adConfig = inlineAdConfigs[adConfigIndex];
      adConfigIndex++;

      if (!shouldShowAd(adConfig)) {
        // Config says do not show — remove the placeholder
        article.body.splice(i, 1);
        i--;
        continue;
      }

      // Replace placeholder with injected ad element
      const inlineAd: InlineAd = {
        type: 'ad',
        ad: {
          ...adConfig,
          injected: true
        }
      };

      article.body.splice(i, 1, inlineAd);
    }
  }
}
